import { useEffect, useState } from "react";
import { getActuals } from "@/utils/budgetApi";
import BudgetGridReadOnly from "@/components/budget/BudgetGridReadOnly";
import BudgetResults from "@/components/budget/BudgetResults";

const eur = (n) =>
  new Intl.NumberFormat("es-ES", { style: "currency", currency: "EUR" }).format(n ?? 0);

function ym(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}
function addMonths(yyyymm, delta) {
  let [y, m] = [parseInt(yyyymm.slice(0, 4)), parseInt(yyyymm.slice(5, 7)) - 1 + delta];
  y += Math.floor(m / 12); m = ((m % 12) + 12) % 12;
  return `${y}-${String(m + 1).padStart(2, "0")}`;
}

export default function ActualsView() {
  const now = new Date();
  const [start, setStart] = useState(`${now.getFullYear()}-01`);
  const [end, setEnd] = useState(ym(now));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let alive = true;
    setLoading(true); setError(null);
    getActuals(start, end)
      .then((d) => { if (alive) setData(d); })
      .catch(() => { if (alive) setError("No se pudo cargar el histórico."); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [start, end]);

  function preset(kind) {
    const cur = ym(new Date());
    if (kind === "ytd") { setStart(`${new Date().getFullYear()}-01`); setEnd(cur); }
    if (kind === "12m") { setStart(addMonths(cur, -11)); setEnd(cur); }
    if (kind === "lastyear") { const y = new Date().getFullYear() - 1; setStart(`${y}-01`); setEnd(`${y}-12`); }
  }

  const ins = data?.insights;

  return (
    <div className="p-6 max-w-[1400px] mx-auto">
      <div className="flex flex-wrap items-end gap-3 mb-4">
        <label className="text-sm text-gray-600 flex items-center gap-1">
          Desde <input type="month" value={start} onChange={(e) => setStart(e.target.value)}
            className="border border-slate-200 rounded-md px-2 py-1.5 text-sm" />
        </label>
        <label className="text-sm text-gray-600 flex items-center gap-1">
          Hasta <input type="month" value={end} onChange={(e) => setEnd(e.target.value)}
            className="border border-slate-200 rounded-md px-2 py-1.5 text-sm" />
        </label>
        <div className="flex gap-1">
          <button onClick={() => preset("ytd")} className="text-xs border rounded-md px-2 py-1.5 hover:bg-slate-50">Lo que va de año</button>
          <button onClick={() => preset("12m")} className="text-xs border rounded-md px-2 py-1.5 hover:bg-slate-50">Últimos 12 meses</button>
          <button onClick={() => preset("lastyear")} className="text-xs border rounded-md px-2 py-1.5 hover:bg-slate-50">Año pasado</button>
        </div>
      </div>

      {ins && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
          <Kpi label="Mejor mes" value={ins.best_month ? `${ins.best_month.month}` : "—"} sub={ins.best_month ? eur(ins.best_month.result) : ""} />
          <Kpi label="Peor mes" value={ins.worst_month ? `${ins.worst_month.month}` : "—"} sub={ins.worst_month ? eur(ins.worst_month.result) : ""} danger />
          <Kpi label="Margen acumulado" value={`${ins.margin_accumulated ?? 0}%`} />
          <Kpi label="Concentración cliente" value={ins.client_concentration ? `${ins.client_concentration.pct}%` : "—"} sub={ins.client_concentration?.client ?? ""} />
          <Kpi label="Runway" value={ins.runway_months != null ? `${ins.runway_months} meses` : "—"} />
        </div>
      )}

      {ins?.low_cash?.months?.length > 0 && (
        <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
          ⚠️ Meses con caja baja (por debajo de {eur(ins.low_cash.threshold)}): {ins.low_cash.months.join(", ")}.
        </div>
      )}

      {error && <p className="text-red-600 text-sm mb-4">{error}</p>}
      {loading && <p className="text-gray-400 mb-4">Cargando…</p>}

      {data && (
        <>
          <div className="mb-6">
            <BudgetResults computed={data.computed} />
          </div>
          <BudgetGridReadOnly budget={data.budget} computed={data.computed} start={start} end={end} />
        </>
      )}
    </div>
  );
}

function Kpi({ label, value, sub, danger }) {
  return (
    <div className="border rounded-lg p-3 bg-white">
      <div className="text-xs text-gray-500">{label}</div>
      <div className={`text-lg font-semibold ${danger ? "text-red-700" : "text-slate-800"}`}>{value}</div>
      {sub ? <div className="text-xs text-gray-400 truncate">{sub}</div> : null}
    </div>
  );
}
